import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/content";

export const FinalCTA = ({ onStartProject }) => {
    return (
        <section
            id="start-project"
            className="relative py-24 lg:py-32 overflow-hidden"
            aria-labelledby="final-cta-heading"
        >
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(900px circle at 25% 20%, rgba(37,99,235,0.10), transparent 55%), radial-gradient(800px circle at 80% 80%, rgba(187,118,33,0.10), transparent 60%)",
                }}
            />
            <div className="absolute inset-0 dot-grid-fine opacity-40 pointer-events-none" />
            <div className="mx-auto max-w-7xl container-px relative">
                <div className="relative overflow-hidden rounded-[28px] border border-black/[0.08] bg-white shadow-deep">
                    {/* Background motif image */}
                    <img
                        src={IMAGES.motifSpark}
                        alt=""
                        aria-hidden="true"
                        className="absolute -right-10 -bottom-10 w-[60%] max-w-[640px] opacity-25 blur-sm pointer-events-none select-none"
                    />
                    <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[rgba(37,99,235,0.10)] blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-20 h-80 w-80 rounded-full bg-[rgba(187,118,33,0.08)] blur-3xl pointer-events-none" />

                    <div className="relative p-10 sm:p-14 lg:p-20">
                        <motion.span
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10% 0px" }}
                            transition={{ duration: 0.4 }}
                            className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))] shadow-[0_0_10px_rgba(187,118,33,0.65)] pulse-dot" />
                            Ready when you are
                        </motion.span>
                        <motion.h2
                            id="final-cta-heading"
                            initial={{ opacity: 0, y: 16, filter: "blur(8px)" }}
                            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                            viewport={{ once: true, margin: "-10% 0px" }}
                            transition={{ duration: 0.6 }}
                            className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.0] tracking-[-0.02em] text-[hsl(var(--text))] max-w-4xl"
                        >
                            Build your next{" "}
                            <span className="italic text-[hsl(var(--text-muted))]">growth engine</span>{" "}
                            with IdeaClap.
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10% 0px" }}
                            transition={{ duration: 0.5, delay: 0.05 }}
                            className="mt-6 max-w-xl text-[15.5px] sm:text-[17px] leading-relaxed text-[hsl(var(--text-muted))]"
                        >
                            Tell us what you’re trying to build. We’ll come back with a point of view, a senior partner, and — if it’s a fit — a small team shaped to your outcome.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10% 0px" }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="mt-10 flex flex-wrap items-center gap-3"
                        >
                            <button
                                type="button"
                                onClick={onStartProject}
                                data-testid="final-cta-start-project-button"
                                className="sheen group inline-flex items-center gap-2 rounded-full bg-[hsl(var(--text))] hover:bg-[hsl(var(--text))]/90 text-[hsl(var(--bg))] px-6 py-3.5 text-[14.5px] font-semibold transition-colors"
                            >
                                Start a Project
                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                            </button>
                            <a
                                href="#capabilities"
                                className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 text-[hsl(var(--text))] px-6 py-3.5 text-[14.5px] font-semibold transition-colors"
                            >
                                Explore Capabilities
                            </a>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FinalCTA;
